<script setup>
import MixGridTile from '@components/mix-grid-tile'

defineProps({
    mixes: {
        type: Array,
        default: () => [],
    },
    scroll: {
        type: Boolean,
        default: false,
    },
    scrollMobileOnly: {
        type: Boolean,
        default: false,
    },
    padded: {
        type: Boolean,
        default: false,
    },
})
</script>

<template>
    <div
        :class="[
            'mix-grid-block',
            scroll && 'mix-grid-block-scroll',
            scrollMobileOnly && 'mix-grid-block-scroll-mobile-only',
            padded && 'mix-grid-block-padded',
        ]"
    >
        <div
            v-for="(mix, index) in mixes"
            :key="'mix-grid-tile' + mix.id + index + Math.random()"
            class="tile"
        >
            <MixGridTile
                :mix="mix"
                :index="index"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

// Net inset = 2×gutter padding − 2×negative-margin correction
// gutter() = $size-large-grid-padding (24px), negative margin = $size-grid-padding (12px) each side
// net = 2×24 − 2×12 = 24px — the amount by which actual block width is narrower than the viewport-derived content width.
$_tile-net-inset: 2 * $size-large-grid-padding - 2 * $size-grid-padding;

.mix-grid-block {
    display: flex;
    flex-wrap: wrap;
    margin-right: -$size-grid-padding;
    margin-left: -$size-grid-padding;

    --mix-grid-columns: 2;
    --mix-grid-reference-width: calc(100vw - #{$_tile-net-inset});

    @include tablet {
        --mix-grid-columns: 3;
    }

    @include desktop {
        --mix-grid-columns: 6;
        // Subtract the sidebar and the net inset so the reference width equals
        // the real block width in padded full-width contexts at every viewport size.
        --mix-grid-reference-width: min(
            calc(100vw - #{$size-large-side-bar} - #{$_tile-net-inset}),
            #{$s-mw - $_tile-net-inset}
        );
    }

    &-padded {
        @include sidePadding;
    }
}

.tile {
    box-sizing: border-box;
    flex: 0 0 calc(var(--mix-grid-reference-width) / var(--mix-grid-columns));
    padding: 0 $size-grid-padding;
    margin-bottom: $size-grid-padding * 2;
}

.mix-grid-block-scroll {
    @include scrollableWrapper;
}

.mix-grid-block-scroll-mobile-only {
    padding: 0;
    .tile {
        box-sizing: content-box;
        flex-basis: 35%;

        &:first-of-type {
            margin-left: $size-grid-padding * 2;
        }
        &:last-of-type {
            padding-right: $size-grid-padding * 3;
        }

        @include tablet {
            flex-basis: 33.3333%;
        }

        @include desktop {
            box-sizing: border-box;
            flex-basis: 16.6666%;
            &:first-of-type {
                margin-left: 0;
            }
            &:last-of-type {
                padding-right: $size-grid-padding;
            }
        }
    }

    @include desktop {
        flex-wrap: wrap;
        &.mix-grid-block-padded {
            @include sidePadding;
        }
    }
}
</style>
